import { jwtDecode, JwtPayload } from "jwt-decode";

interface TokenPayload extends JwtPayload {
    exp: number;
}

interface UseTokenActionsReturn {
    isTokenValid: (token: string | null) => boolean;
    isTokenExpired: (token: string | null) => boolean;
    retrieveTokenFromStorage: (storageKey: string) => string | null;
    setTokenInStorage: (storageKey: string, token: string | null) => void;
    decodeToken: (token: string | null) => TokenPayload | null;
}

export function useTokenActions(): UseTokenActionsReturn {
    function isTokenValid(token: string | null): boolean {
        if (!token) {
            console.warn("🟡 isTokenValid: Token is null/empty");
            return false;
        }
        try {
            const decoded = jwtDecode<TokenPayload>(token);
            const hasExp = !!decoded.exp;
            return hasExp;
        } catch (error) {
            console.error("🔴 isTokenValid: Failed to decode JWT");
            console.error("   - Error:", error instanceof Error ? error.message : error);
            console.error("   - Token preview:", token.substring(0, 50) + "...");
            return false;
        }
    }

    function isTokenExpired(token: string | null): boolean {
        if (!token) {
            console.warn("🟡 isTokenExpired: Token is null/empty");
            return true;
        }
        try {
            const decoded = jwtDecode<TokenPayload>(token);
            const currentTime = new Date().getTime() / 1000;
            const expirationTime = decoded.exp || 0;
            const isExpired = currentTime > expirationTime;
            return isExpired;
        } catch (error) {
            console.error("🔴 isTokenExpired: Failed to decode JWT");
            console.error("   - Error:", error instanceof Error ? error.message : error);
            return true;
        }
    }

    function retrieveTokenFromStorage(storageKey: string): string | null {
        return localStorage.getItem(storageKey);
    }

    function setTokenInStorage(storageKey: string, token: string | null): void {
        localStorage.setItem(storageKey, token || "");
    }

    function decodeToken(token: string | null): TokenPayload | null {
        if (!token) return null;
        try {
            return jwtDecode<TokenPayload>(token);
        } catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    }

    return { isTokenValid, isTokenExpired, retrieveTokenFromStorage, setTokenInStorage, decodeToken };
}
